// Shared types and API helpers for the repair domain.
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api/client";

// ── Domain types ──────────────────────────────────────────────────────────────

export interface RepairTicket {
  id: string;
  code: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  serialId?: string;
  serialNo?: string;
  warrantyClaimId?: string;
  deviceDesc: string;
  accessories?: string;
  issueDesc: string;
  diagnosis?: string;
  technicianId?: string;
  technicianEmail?: string;
  receivedAt: string;
  receivedBy?: string;
  status: string;
  estimatedCost: string;
  partsCost: string;
  laborCost: string;
  finalCost: string;
  paidAmount: string;
  deliveredAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RepairPart {
  id: string;
  ticketId: string;
  variantId: string;
  variantSku?: string;
  productName?: string;
  quantity: number;
  unitPrice: string;
  lineTotal: string;
  stockApplied: boolean;
  note?: string;
  createdAt: string;
}

export interface RepairLabor {
  id: string;
  ticketId: string;
  description: string;
  amount: string;
  performedBy?: string;
  createdAt: string;
}

export interface RMARequest {
  id: string;
  code: string;
  ticketId?: string;
  serialId: string;
  serialNo?: string;
  vendor: string;
  vendorRef?: string;
  sentAt: string;
  sentBy?: string;
  receivedBackAt?: string;
  status: string;
  result?: string;
  note?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TicketDetail {
  ticket: RepairTicket;
  parts: RepairPart[];
  labor: RepairLabor[];
  rMAs: RMARequest[];
}

// ── Status labels ─────────────────────────────────────────────────────────────

export const STATUS_LABELS: Record<string, string> = {
  received:      "Tiếp nhận",
  diagnosing:    "Chẩn đoán",
  quoted:        "Đã báo giá",
  approved:      "Đã duyệt",
  repairing:     "Đang sửa",
  waiting_parts: "Chờ phụ tùng",
  sent_to_vendor:"Gửi hãng",
  received_back: "Nhận lại",
  done:          "Hoàn thành",
  delivered:     "Đã giao",
  cancelled:     "Đã huỷ",
};

export const STATUS_COLORS: Record<string, string> = {
  received:      "bg-blue-100 text-blue-700",
  diagnosing:    "bg-purple-100 text-purple-700",
  quoted:        "bg-yellow-100 text-yellow-700",
  approved:      "bg-indigo-100 text-indigo-700",
  repairing:     "bg-orange-100 text-orange-700",
  waiting_parts: "bg-amber-100 text-amber-700",
  sent_to_vendor:"bg-cyan-100 text-cyan-700",
  received_back: "bg-teal-100 text-teal-700",
  done:          "bg-green-100 text-green-700",
  delivered:     "bg-emerald-100 text-emerald-700",
  cancelled:     "bg-gray-100 text-gray-500",
};

// FSM: allowed transitions per status.
export const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  received:      ["diagnosing", "cancelled"],
  diagnosing:    ["quoted", "cancelled"],
  quoted:        ["approved", "cancelled"],
  approved:      ["repairing", "cancelled"],
  repairing:     ["waiting_parts", "sent_to_vendor", "done", "cancelled"],
  waiting_parts: ["repairing", "cancelled"],
  sent_to_vendor:["received_back", "cancelled"],
  received_back: ["repairing", "cancelled"],
  done:          ["delivered"],
  delivered:     [],
  cancelled:     [],
};

// ── API functions ─────────────────────────────────────────────────────────────

export interface ListTicketsParams {
  status?: string;
  technicianId?: string;
  customerId?: string;
  from?: string;
  to?: string;
  q?: string;
  page?: number;
  limit?: number;
}

export const repairApi = {
  listTickets: (params?: ListTicketsParams) =>
    apiGet<RepairTicket[]>("/v1/repair/tickets", params),

  kanban: () =>
    apiGet<Record<string, RepairTicket[]>>("/v1/repair/tickets/kanban"),

  getTicket: (id: string) =>
    apiGet<TicketDetail>(`/v1/repair/tickets/${id}`),

  createTicket: (body: {
    warrantyClaimId?: string;
    customerId?: string;
    customerPhone?: string;
    customerName?: string;
    serialId?: string;
    deviceDesc?: string;
    accessories?: string;
    issueDesc: string;
    technicianId?: string;
    estimatedCost?: string;
  }) => apiPost<RepairTicket>("/v1/repair/tickets", body),

  patchTicket: (id: string, body: Partial<RepairTicket>) =>
    apiPatch<RepairTicket>(`/v1/repair/tickets/${id}`, body),

  transition: (id: string, status: string) =>
    apiPost<RepairTicket>(`/v1/repair/tickets/${id}/transition`, { status }),

  addPart: (ticketId: string, body: { variantId: string; quantity: number; unitPrice: string; note?: string }) =>
    apiPost<RepairPart>(`/v1/repair/tickets/${ticketId}/parts`, body),

  deletePart: (partId: string) =>
    apiDelete(`/v1/repair/parts/${partId}`),

  addLabor: (ticketId: string, body: { description: string; amount: string }) =>
    apiPost<RepairLabor>(`/v1/repair/tickets/${ticketId}/labor`, body),

  deleteLabor: (laborId: string) =>
    apiDelete(`/v1/repair/labor/${laborId}`),

  createRMA: (ticketId: string, body: { serialId: string; vendor: string; vendorRef?: string; note?: string }) =>
    apiPost<RMARequest>(`/v1/repair/tickets/${ticketId}/rma`, body),

  receiveRMABack: (rmaId: string, result: string) =>
    apiPost<RMARequest>(`/v1/repair/rma/${rmaId}/receive-back`, { result }),

  listRMA: (params?: { status?: string }) =>
    apiGet<RMARequest[]>("/v1/repair/rma", params),

  print: (ticketId: string, workstationId: string) =>
    apiPost<{ status: string }>(`/v1/repair/tickets/${ticketId}/print?workstation_id=${workstationId}`),
};

// ── Helpers ───────────────────────────────────────────────────────────────────

export function fmtVND(s?: string | null): string {
  if (!s) return "0";
  const n = Math.round(parseFloat(s));
  return n.toLocaleString("vi-VN");
}

export function fmtDate(d?: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleString("vi-VN", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}
